// Konva renders to <canvas>, not the DOM, so it can't read CSS variables.
// This file mirrors globals.css for canvas-side code (ShapeRenderer, lasso,
// selection ring). DOM components use Tailwind classes against the same vars
// — globals.css is the source of truth. If you change a value here, change it
// there too.
//
// Modern browsers accept oklch() in canvas fillStyle/strokeStyle (Chrome 111+,
// Safari 15.4+, Firefox 113+). We pass them straight through.

import type { StickyColor } from '@frame/shared';

export type Theme = 'light' | 'dark';

export interface CanvasTokens {
  bgApp: string;
  bgCanvas: string;
  fgPrimary: string;
  fgSecondary: string;
  fgTertiary: string;
  borderDefault: string;
  borderStrong: string;
  accent: string;
  accentSubtle: string;
  sticky: Record<StickyColor, string>;
  stickyText: string;
  stickyShadow: { color: string; opacity: number; blur: number; offsetY: number };
  selection: string;
  lassoFill: string;
  lassoStroke: string;
  // Cursor palette per spec §2.7 / globals.css `--cursor-1..8`. Identical in
  // light and dark — presence is participant-scoped, not theme-scoped.
  cursors: readonly string[];
}

// Canonical 8-color cursor palette. Assign by `participantId.charCodeAt sum % 8`.
export const CURSOR_PALETTE: readonly string[] = [
  'oklch(0.62 0.16 25)',   // coral
  'oklch(0.65 0.14 60)',   // amber
  'oklch(0.62 0.13 100)',  // olive
  'oklch(0.62 0.13 145)',  // jade
  'oklch(0.58 0.14 200)',  // teal
  'oklch(0.55 0.15 265)',  // indigo
  'oklch(0.58 0.16 310)',  // magenta
  'oklch(0.60 0.14 350)',  // rose
];

export const LIGHT_TOKENS: CanvasTokens = {
  bgApp: '#FFFFFF',
  bgCanvas: '#FAFAFA',
  fgPrimary: '#18181B',
  fgSecondary: '#52525B',
  fgTertiary: '#71717A',
  borderDefault: '#E4E4E7',
  borderStrong: '#D4D4D8',
  accent: 'oklch(0.55 0.13 250)',
  accentSubtle: 'oklch(0.97 0.02 250)',
  sticky: {
    sand: '#F4E9D0',
    clay: '#E8C9B0',
    rose: '#ECCFCE',
    dusk: '#D9CCE3',
    fog: '#CFD7DE',
    sage: '#C9D6C2',
    moss: '#C8CFAF',
    ochre: '#E2D6A1',
  },
  stickyText: '#2A2724',
  stickyShadow: { color: '#18181B', opacity: 0.12, blur: 8, offsetY: 3 },
  selection: 'oklch(0.55 0.13 250)',
  lassoFill: 'rgba(80, 103, 173, 0.08)',
  lassoStroke: 'oklch(0.55 0.13 250)',
  cursors: CURSOR_PALETTE,
};

export const DARK_TOKENS: CanvasTokens = {
  bgApp: '#0F0F13',
  bgCanvas: '#08080B',
  fgPrimary: '#FAFAFA',
  fgSecondary: '#B4B4BC',
  fgTertiary: '#8E8E98',
  borderDefault: '#383841',
  borderStrong: '#4A4A55',
  accent: 'oklch(0.68 0.13 250)',
  accentSubtle: 'oklch(0.30 0.08 250)',
  sticky: {
    sand: '#DCC78F',
    clay: '#CFA686',
    rose: '#D49E9D',
    dusk: '#B79EC9',
    fog: '#9FAEC2',
    sage: '#9FBC92',
    moss: '#B0BB80',
    ochre: '#CFBA68',
  },
  stickyText: '#2A2724',
  stickyShadow: { color: '#000000', opacity: 0.6, blur: 10, offsetY: 4 },
  selection: 'oklch(0.68 0.13 250)',
  lassoFill: 'rgba(138, 154, 217, 0.10)',
  lassoStroke: 'oklch(0.68 0.13 250)',
  cursors: CURSOR_PALETTE,
};

export const TOKENS: Record<Theme, CanvasTokens> = {
  light: LIGHT_TOKENS,
  dark: DARK_TOKENS,
};
